import * as React from "react";
import { Dialog } from "@nozzlegear/react-win-dialog";
import { MaybeError } from "shared/components/maybe-error";
import { useLoadingState } from "client/hooks/use-loading-state";
import { useState } from "react";
import { AdminClient, ApiError } from "client/http";
import { Auth } from "client/stores";
import { UserSummary } from "api";
import { useUnauthorizedPrompt } from "client/hooks";

type Props = {
    open: boolean;
    onResultsFound: (users: UserSummary[]) => void;
    onClose: () => void;
};

export function SearchDialog(props: Props): React.ReactElement {
    const client = React.useMemo(() => new AdminClient(Auth.token.value), [Auth.token.value]);
    const handleUnauthorized = useUnauthorizedPrompt();
    const [{loading, error}, setLoadingState] = useLoadingState(false);
    const [locationId, setLocationId] = useState("");

    const beginSearch = React.useCallback(async (e: React.SyntheticEvent) => {
        e.preventDefault();

        if (loading)
            return;
        if (!locationId)
            return setLoadingState("You must enter a Location ID for the search.");

        setLoadingState(true);

        try {
            const searchResult = await client.findUser(locationId);

            if (searchResult.users.length > 0) {
                props.onResultsFound(searchResult.users);
                setLoadingState(false);
                props.onClose();
            } else {
                setLoadingState("No user was found with a Location ID matching " + searchResult.locationIds.join(", or "));
            }
        } catch (e: unknown) {
            console.error("Failed to search for user:", e);

            if (e instanceof ApiError) {
                if (e.unauthorized && handleUnauthorized(window.location.pathname)) {
                    return;
                }
                setLoadingState(e.message);
            } else if (e instanceof Error) {
                setLoadingState(e.message);
            } else {
                setLoadingState("Something went wrong and the search could not be completed.");
            }
        }

    }, [loading, locationId, setLoadingState, props.onResultsFound, handleUnauthorized, client]);

    const handleLocationIdChange = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        if (loading)
            return;
        setLocationId(e.currentTarget.value);
    }, [loading, setLocationId]);

    if (!props.open)
        return <React.Fragment />;

    return (
        <Dialog
            id="search-dialog"
            title="Find User"
            open={props.open}
            loading={loading}
            primaryText="Search"
            secondaryText="Close"
            onPrimaryClick={beginSearch}
            onSecondaryClick={props.onClose}
            overlayPreventsScrolling={false}
        >
            <form onSubmit={beginSearch}>
                <div className="control-group">
                    <label htmlFor="location-id">{"Location ID"}</label>
                    <input
                        type="text"
                        name="location-id"
                        autoComplete="off"
                        value={locationId}
                        onChange={handleLocationIdChange}
                    />
                </div>
                <MaybeError error={error} />
            </form>
        </Dialog>
    );
}
